import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormGroup,
  FormLabel,
  Grid,
  OutlinedInput,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import Api from "lib/api/httpRequestHelper";
import componentStyles from "assets/theme/views";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles({
  ...componentStyles,
  cardRoot: {
    marginBottom: "1.5rem",
    padding: "1rem",
  },
  errorText: {
    color: "red",
    paddingLeft: "12px",
    display: "flex",
    alignItems: "center",
  },
});

const ESPECIE_OPTIONS = [
  { id: 0, value: "Rolo" },
  { id: 1, value: "Caixa" },
];
export default function SolicitarDevolucaoStep2({
  solicitacao,
  groupName,
  socialName,
  cnpj,
  selectedReasonComplete,
  handleClear,
  setStep,
  setItem,
}) {
  const {
    Id: idSolicitacao,
    QuantidadeItens: volumeQuantity,
    VolumePesoLiquido: pesoLiquido,
  } = solicitacao || {};

  const [formData, setFormData] = useState({
    especie: "",
    pesoBruto: "",
    email: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWeightError, setShowWeightError] = useState(false);

  const classes = useStyles();
  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "pesoBruto") {
      setShowWeightError(false);
    }
  };

  const handleEspecieChange = (value) => {
    setFormData((prev) => ({ ...prev, especie: value }));
  };

  const handleReturn = () => {
    setStep(1);
  };

  const sendEmailNotification = async () => {
    try {
      const res = await Api.get(
        "send-email-request",
        "trocasDevolucoes",
        `?idSolicitacao=${idSolicitacao}&emailEnvio=${formData.email}`
      );

      if (res.Success) {
        snackbar.showMessage(res.Result.MessageResponse, {
          severity: "success",
        });
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  const handleSubmit = async () => {
    if (parseFloat(formData.pesoBruto) < parseFloat(pesoLiquido)) {
      setShowWeightError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const body = {
        idSolicitacao,
        statusSolicitacao: "ABERTA",
        volumeTipo: formData.especie.value.toUpperCase(),
        pesoBruto: Number(formData.pesoBruto),
        emailEnvio: formData.email,
      };

      const res = await Api.put(
        "edit-request-reason",
        "trocasDevolucoes",
        body,
        true
      );

      if (res.Success) {
        await sendEmailNotification();
        setItem([]);
        snackbar.showMessage(res.Result.MessageResponse, {
          severity: "success",
        });
        setFormData({ especie: "", pesoBruto: "", email: "" });
        handleClear();
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Container component={Box} style={{ marginTop: "-4rem" }} maxWidth={true}>
        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Typography variant="h2">Informações da solicitação</Typography>
            <Grid container spacing={3} style={{ marginTop: "1rem" }}>
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography variant="h4">Grupo</Typography>
                <Typography variant="body2">{groupName?.Nome || ""}</Typography>
              </Grid>
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography variant="h4">Razão Social</Typography>
                <Typography variant="body2">
                  {socialName.Nome ? socialName.Nome.split("|")[0].trim() : ""}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography variant="h4">CNPJ</Typography>
                <Typography variant="body2">{cnpj || ""}</Typography>
              </Grid>
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography variant="h4">Motivo Devolução</Typography>
                <Typography variant="body2">
                  {selectedReasonComplete?.Motivo || ""}
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={isMobile ? 12 : 6}>
                <FormGroup>
                  <FormLabel>E-mail de contato</FormLabel>
                  <OutlinedInput
                    fullWidth
                    type="email"
                    disabled={isSubmitting}
                    value={formData.email}
                    onChange={handleInputChange("email")}
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Typography variant="h2">Informações de Volumes</Typography>

            <Grid container spacing={3} style={{ marginTop: "1rem" }}>
              <Grid item xs={isMobile ? 12 : 6}>
                <FormGroup>
                  <FormLabel>Quantidade de Volumes</FormLabel>
                  <OutlinedInput
                    readOnly
                    fullWidth
                    disabled
                    type="number"
                    value={volumeQuantity || ""}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={isMobile ? 12 : 6}>
                <FormGroup
                  data-tut="reactour__step4"
                  style={{ marginBottom: "-1.5rem" }}
                >
                  <AutoCompleteField
                    label="Especie"
                    onChange={handleEspecieChange}
                    value={formData.especie}
                    options={ESPECIE_OPTIONS}
                    disabled={isSubmitting}
                    optionLabelKey="value"
                    optionIdKey="id"
                  />
                </FormGroup>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={isMobile ? 12 : 6}>
                <FormGroup>
                  <FormLabel>Peso Líquido</FormLabel>
                  <OutlinedInput
                    fullWidth
                    readOnly
                    disabled
                    type="number"
                    value={pesoLiquido || ""}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={isMobile ? 12 : 6}>
                <FormGroup>
                  <FormLabel>Peso Bruto</FormLabel>
                  <OutlinedInput
                    fullWidth
                    disabled={isSubmitting}
                    type="number"
                    value={formData.pesoBruto}
                    onChange={handleInputChange("pesoBruto")}
                    inputProps={{ min: pesoLiquido }}
                  />
                </FormGroup>
              </Grid>

              {showWeightError && (
                <Typography
                  variant="h5"
                  component="h5"
                  style={{ color: "grey", paddingLeft: "12px" }}
                >
                  <i
                    className="fa fa-exclamation-circle"
                    aria-hidden="true"
                    style={{ marginRight: "8px" }}
                  />{" "}
                  O peso bruto deve ser maior que o peso líquido
                </Typography>
              )}
            </Grid>

            <Grid
              container
              style={{ justifyContent: "flex-end", marginTop: "1rem" }}
            >
              <Button
                style={{
                  width: isMobile ? "100%" : "15vw",
                  marginBottom: isMobile ? "0.75rem" : "0rem",
                  marginTop: isMobile ? "0.75rem" : "0",
                  marginRight: isMobile ? "0" : "1rem",
                }}
                variant="outlined"
                onClick={(e) => {
                  handleReturn();
                }}
                disabled={isSubmitting}
              >
                Voltar
              </Button>
              <Button
                style={{
                  width: isMobile ? "100%" : "15vw",
                  marginBottom: isMobile ? "0.75rem" : "0rem",
                  marginTop: isMobile ? "0.75rem" : "0",
                  alignSelf: "center",
                }}
                variant="contained"
                onClick={(e) => {
                  handleSubmit();
                }}
                disabled={
                  !formData.especie ||
                  !formData.pesoBruto ||
                  !formData.email ||
                  isSubmitting
                }
              >
                Avançar
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
